import { useMemo, useState } from 'react';
import { Anchor, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { RecommendedFreightClassModal } from './RecommendedFreightClassModal';
import { WizardCommodityCard } from './WizardCommodityCard';
import { calculateDensity } from '../../../../api/ltl.api';
import { PACKAGING_TYPE_OPTIONS } from '../../../../ltl/constants';
import { mapDimensionUnit, mapHandlingUnitsToPayload, mapWeightUnit } from '../../../../ltl/freight-payload-mappers';
import { CustomNumberInput, DimensionsInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { createEmptyCommodity } from '../../create-empty-handling-unit';

import type { Dimensions } from '../../../../types/dimensions';
import type { FreightHandlingUnitDensityResult } from '../../../../types/freight-responses';
import type { FreightPackagingType, LtlCommodity, LtlHandlingUnit } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';

export type WizardHandlingUnitCardProps = {
  unit: LtlHandlingUnit;
  index: number;
  displayWeightUnit: WeightUnit;
  sellerFreightProviderId: number;
  onChange: (next: LtlHandlingUnit) => void;
  onRemove?: () => void;
  productSuggestions?: string[];
  onProductSearch?: (query: string) => void;
  onProductSelect?: (commodityId: string, productName: string) => void;
};

export const WizardHandlingUnitCard = ({
  unit,
  index,
  displayWeightUnit,
  sellerFreightProviderId,
  onChange,
  onRemove,
  productSuggestions,
  onProductSearch,
  onProductSelect,
}: WizardHandlingUnitCardProps) => {
  const intl = useIntl();

  const [densityResult, setDensityResult] = useState<FreightHandlingUnitDensityResult | null>(null);
  const [isCalculatingDensity, setIsCalculatingDensity] = useState(false);
  const [isDensityModalOpen, { open: openDensityModal, close: closeDensityModal }] = useDisclosure(false);

  const packagingTypeData = useMemo(
    () => PACKAGING_TYPE_OPTIONS.map((option) => ({ value: option.value, label: intl.formatMessage(option.label) })),
    [intl],
  );

  const dimensionsValue: Dimensions = {
    length: unit.length,
    width: unit.width,
    height: unit.height,
    unit: (unit.dimensionUnit as Dimensions['unit']) ?? 'Inches',
  };

  const handleQuantityChange = (value: number | string) => {
    const parsed = typeof value === 'string' ? parseInt(value, 10) || 1 : value;
    onChange({ ...unit, quantity: parsed });
  };

  const handleTypeChange = (value: string | null) => {
    if (value) onChange({ ...unit, packagingType: value as FreightPackagingType });
  };

  const handleStackableChange = (value: string | null) => onChange({ ...unit, isStackable: value === 'yes' });

  const handleDimensionsChange = (next: Dimensions) => onChange({ ...unit, length: next.length, width: next.width, height: next.height });

  const handleCommodityChange = (commodityIndex: number, updated: LtlCommodity) => {
    const commodities = [...unit.commodities];
    commodities[commodityIndex] = updated;
    onChange({ ...unit, commodities });
  };

  const handleAddCommodity = () =>
    onChange({ ...unit, commodities: [...unit.commodities, createEmptyCommodity(unit.commodities, displayWeightUnit)] });

  const handleRemoveCommodity = (commodityIndex: number) =>
    onChange({ ...unit, commodities: unit.commodities.filter((_, idx) => idx !== commodityIndex) });

  const handleCalculateDensity = async () => {
    setIsCalculatingDensity(true);
    try {
      const weightUnit = mapWeightUnit(unit.commodities[0]?.weightUnit ?? displayWeightUnit);
      const dimensionUnit = mapDimensionUnit(unit.dimensionUnit ?? 'Inches');
      const [mappedUnit] = mapHandlingUnitsToPayload([unit], weightUnit, dimensionUnit);
      const response = await calculateDensity({ sellerFreightProviderId, handlingUnits: [mappedUnit] });
      setDensityResult(response.handlingUnitList?.[0] ?? null);
      openDensityModal();
    } catch {
      setDensityResult(null);
      openDensityModal();
    } finally {
      setIsCalculatingDensity(false);
    }
  };

  const handleApplyDensityRecommendations = (indices: number[]) => {
    if (!densityResult) return;
    const indexSet = new Set(indices);
    const updatedCommodities = unit.commodities.map((c, idx) => {
      if (!indexSet.has(idx)) return c;
      const rec = densityResult.commodities[idx]?.recommendedFreightClass;
      return rec ? { ...c, freightClass: rec } : c;
    });
    onChange({ ...unit, commodities: updatedCommodities });
  };

  return (
    <Paper data-testid={`handling-unit-card-${index}`} p='md' shadow='none' withBorder>
      <Stack gap='sm'>
        <Group justify='space-between'>
          <Text fw={600}>{intl.formatMessage(handlingUnitsStepMessages.handlingUnitTitle, { number: index + 1 })}</Text>
          <Group gap='xs'>
            <Button
              data-testid={`calculate-density-${index}`}
              loading={isCalculatingDensity}
              onClick={handleCalculateDensity}
              size='xs'
              variant='tertiary'
            >
              {intl.formatMessage(handlingUnitsStepMessages.calculateDensity)}
            </Button>
            {onRemove ? (
              <Button color='red' data-testid={`remove-unit-${index}`} onClick={onRemove} size='xs' variant='ghost'>
                {intl.formatMessage(handlingUnitsStepMessages.removeHandlingUnit)}
              </Button>
            ) : null}
          </Group>
        </Group>

        <Group grow>
          <CustomNumberInput
            allowNegative={false}
            data-testid={`unit-quantity-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.quantityLabel)}
            min={1}
            onChange={handleQuantityChange}
            value={unit.quantity}
          />
          <Dropdown
            data={packagingTypeData}
            data-testid={`unit-type-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.unitTypeLabel)}
            onChange={handleTypeChange}
            value={unit.packagingType}
          />
          <Dropdown
            data={[
              { value: 'yes', label: intl.formatMessage(handlingUnitsStepMessages.stackableYes) },
              { value: 'no', label: intl.formatMessage(handlingUnitsStepMessages.stackableNo) },
            ]}
            data-testid={`unit-stackable-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.stackableLabel)}
            onChange={handleStackableChange}
            value={unit.isStackable ? 'yes' : 'no'}
          />
        </Group>

        <Stack gap={4}>
          <Text fw={500} size='sm'>
            {intl.formatMessage(freightFormMessages.dimensionsLabel, { unit: dimensionsValue.unit })}
          </Text>
          <DimensionsInput onChange={handleDimensionsChange} value={dimensionsValue} />
        </Stack>

        {unit.commodities.map((commodity, cIdx) => (
          <WizardCommodityCard
            commodity={commodity}
            displayWeightUnit={displayWeightUnit}
            index={cIdx}
            key={commodity.id}
            onChange={(updated) => handleCommodityChange(cIdx, updated)}
            onProductSearch={onProductSearch}
            onProductSelect={onProductSelect}
            onRemove={cIdx > 0 ? () => handleRemoveCommodity(cIdx) : undefined}
            productSuggestions={productSuggestions}
          />
        ))}

        <Anchor component='button' data-testid={`add-commodity-${index}`} onClick={handleAddCommodity} size='sm' type='button'>
          {intl.formatMessage(handlingUnitsStepMessages.addCommodity)}
        </Anchor>
      </Stack>

      <RecommendedFreightClassModal
        commodities={unit.commodities}
        commodityResults={densityResult?.commodities ?? []}
        density={densityResult?.density?.value ?? null}
        densityUnit={densityResult?.density?.unit ?? 'lb/ft³'}
        onApply={handleApplyDensityRecommendations}
        onClose={closeDensityModal}
        opened={isDensityModalOpen}
      />
    </Paper>
  );
};
